import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PythagoreanNumerologyCalculator = () => {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleNameChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const handleBirthDateChange = (e) => {
    setBirthDate(e.target.value);
    setError('');
  };

  const calculatePythagoreanNumerology = () => {
    if (!name || !birthDate) {
      setError('Please enter both name and birth date');
      return;
    }

    // Calculate Life Path Number
    const birthDateNumbers = birthDate.split('-').join('');
    let lifePath = birthDateNumbers.split('').reduce((acc, num) => acc + parseInt(num), 0);
    while (lifePath > 9) {
      lifePath = lifePath.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
    }

    // Calculate Expression Number
    const nameNumbers = name
      .toLowerCase()
      .split('')
      .filter(char => char.match(/[a-z]/))
      .map(char => 'abcdefghijklmnopqrstuvwxyz'.indexOf(char) + 1)
      .reduce((acc, num) => acc + num, 0);
    let expression = nameNumbers;
    while (expression > 9) {
      expression = expression.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
    }

    // Calculate Soul Urge Number
    const vowels = 'aeiou';
    const soulNumbers = name
      .toLowerCase()
      .split('')
      .filter(char => vowels.includes(char))
      .map(char => 'aeiou'.indexOf(char) + 1)
      .reduce((acc, num) => acc + num, 0);
    let soulUrge = soulNumbers;
    while (soulUrge > 9) {
      soulUrge = soulUrge.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
    }

    // Prepare chart data
    const numerologyChartData = {
      labels: ['Life Path', 'Expression', 'Soul Urge'],
      datasets: [{
        label: 'Numerology Numbers',
        data: [lifePath, expression, soulUrge],
        borderColor: [
          'rgba(79, 70, 229, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(59, 130, 246, 1)'
        ],
        backgroundColor: [
          'rgba(79, 70, 229, 0.1)',
          'rgba(239, 68, 68, 0.1)',
          'rgba(59, 130, 246, 0.1)'
        ],
        tension: 0.4,
        pointRadius: 8,
        pointHoverRadius: 12,
        pointBackgroundColor: [
          'rgba(79, 70, 229, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(59, 130, 246, 1)'
        ],
        pointBorderColor: 'white',
        pointBorderWidth: 2
      }]
    };

    setResults({
      name,
      birthDate,
      lifePath,
      expression,
      soulUrge
    });

    setChartData(numerologyChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Pythagorean Numerology Calculator | Calculate Your Life Path and Expression Numbers</title>
        <meta name="description" content="Calculate your Pythagorean numerology numbers using our advanced calculator. Understand your life path, expression, and soul urge numbers for personal insight." />
        <meta name="keywords" content="pythagorean numerology, life path calculator, expression number, soul urge number, personal numerology analysis" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/pythagorean-numerology" />
        <meta property="og:title" content="Pythagorean Numerology Calculator | Calculate Your Life Path and Expression Numbers" />
        <meta property="og:description" content="Calculate your Pythagorean numerology numbers using our advanced calculator. Understand your life path, expression, and soul urge numbers for personal insight." />
        <meta property="og:image" content="https://yourwebsite.com/images/pythagorean-numerology-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/pythagorean-numerology" />
        <meta property="twitter:title" content="Pythagorean Numerology Calculator | Calculate Your Life Path and Expression Numbers" />
        <meta property="twitter:description" content="Calculate your Pythagorean numerology numbers using our advanced calculator. Understand your life path, expression, and soul urge numbers for personal insight." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/pythagorean-numerology-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔢</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Pythagorean Numerology Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Life Path and Expression Numbers</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Your Full Name:
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={handleNameChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter your full name"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
              Your Birth Date:
            </label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={birthDate}
              onChange={handleBirthDateChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            />
          </div>
        </div>

        <button
          onClick={calculatePythagoreanNumerology}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white mt-6"
        >
          Calculate Numerology Numbers
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Numerology Profile</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.name}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Birth Date:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.birthDate}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Life Path Number:</h5>
                    <p className="text-purple-600 text-4xl text-center">{results.lifePath}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Expression Number:</h5>
                    <p className="text-purple-600 text-4xl text-center">{results.expression}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Soul Urge Number:</h5>
                    <p className="text-purple-600 text-4xl text-center">{results.soulUrge}</p>
                  </div>
                </div>
              </div>

              {/* Numerology Visualization Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Numerology Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Numerology Number Analysis',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 9,
                          ticks: {
                            stepSize: 1,
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        },
                        x: {
                          ticks: {
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        }
                      },
                      animation: {
                        duration: 1000,
                        easing: 'easeInOutQuart'
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Number Meanings <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Life Path Number</h3>
              <p className="text-gray-700">
                Your Life Path Number reveals your life's purpose and the major lessons you need to learn. It's calculated from your birth date and represents your journey through life.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Expression Number</h3>
              <p className="text-gray-700">
                Your Expression Number shows your natural talents, abilities, and personal characteristics. It's derived from the letters in your full name and represents your potential.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Soul Urge Number</h3>
              <p className="text-gray-700">
                Your Soul Urge Number reveals your inner desires and what truly motivates you. It's calculated from the vowels in your name and represents your heart's deepest yearnings.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Pythagorean numerology:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Information</h3>
              <p className="text-gray-700">
                Enter your full name and birth date in the input fields.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Numerology Numbers" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Life Path, Expression, and Soul Urge numbers to understand your personality, potential, and inner motivations.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Pythagorean Numerology?</h3>
              <p className="text-gray-700">
                Pythagorean Numerology is a system that assigns numerical values to letters and numbers to reveal insights about personality, life purpose, and potential.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How are the numbers calculated?</h3>
              <p className="text-gray-700">
                The calculator uses your birth date and name to calculate three main numbers: Life Path, Expression, and Soul Urge numbers, each revealing different aspects of your personality.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What do the numbers mean?</h3>
              <p className="text-gray-700">
                Each number has unique meanings and interpretations. The visualization graph helps you understand the relationships between your different numerology numbers.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Life Path Number calculation</li>
                <li>Expression Number analysis</li>
                <li>Soul Urge Number interpretation</li>
                <li>Visual number relationships</li>
                <li>Comprehensive explanations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand personality traits</li>
                <li>Discover life purpose</li>
                <li>Enhance self-awareness</li>
                <li>Support personal growth</li>
                <li>Improve decision-making</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PythagoreanNumerologyCalculator;